'use client';

import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { EllipsisVerticalIcon } from 'lucide-react';
import { toast } from 'sonner';

import { EditSkillDialog } from '@/app/(app)/settings/skills/dialog';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type ApiSkill = ApiSchemas['SkillReadComplete'];

export const SKILL_COLUMNS: ColumnDef<ApiSkill>[] = [
  {
    accessorKey: 'name',
    header: () => <div className='p-4'>Name</div>,
    cell: ({ row }) => {
      return <span className='p-4 font-medium'>{row.original.name}</span>;
    },
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      return (
        <Badge variant={row.original.status === 'ACTIVE' ? 'default' : 'outline'} className='lowercase'>
          {row.original.status}
        </Badge>
      );
    },
  },
  {
    header: 'Description',
    accessorKey: 'description',
    cell: ({ row }) => {
      return (
        <div className='max-w-[30vw] truncate text-muted-foreground'>
          <span>{row.original.description || '-'}</span>
        </div>
      );
    },
  },
  {
    header: 'Category',
    accessorKey: 'category',
    cell: ({ row }) => {
      if (!row.original.category) return <span className='text-muted-foreground'>-</span>;
      return <Badge className='capitalize'>{row.original.category.toLowerCase().replace('_', ' ')}</Badge>;
    },
  },
  {
    header: 'Action',
    accessorKey: 'action',
    cell: ({ row }) => {
      if (!row.original.action) return <span className='text-muted-foreground'>-</span>;
      return (
        <Badge variant='outline' className='capitalize'>
          {row.original.action.toLowerCase().replace('_', ' ')}
        </Badge>
      );
    },
  },
  {
    header: 'Pests',
    cell: ({ row }) => {
      const issueCount = row.original.skill_issues?.length || 0;
      return <span className='text-muted-foreground'>{issueCount}</span>;
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      return <ActionCell skill={row.original} />;
    },
  },
];

interface ActionCellProps {
  skill: ApiSkill;
}

function ActionCell({ skill }: ActionCellProps) {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { mutate } = api.skills.removeSkillsSkillIdDelete.useMutation(
    {
      path: { skill_id: skill.id },
    },
    {
      onSuccess: () => {
        toast.success('Skill deleted');
        api.skills.getAllSkillsGet.invalidateQueries();
      },
    }
  );

  const onDelete = () => {
    if (confirm('Are you sure you want to delete this skill?')) {
      mutate();
    }
  };

  return (
    <div className='flex justify-end'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='icon'>
            <EllipsisVerticalIcon className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onSelect={() => setOpen(true)}>Edit</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onDelete} variant='destructive'>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <EditSkillDialog open={open} onOpenChange={setOpen} skill={skill} />
    </div>
  );
}
